var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState, } from "react";
import { Dropdown } from "./Dropdown";
import { TextInput } from "./Input";
import { Radio } from "./Radio";
import { AggregationInterval } from "ee-nestjs-interfaces";
import { alignmentForTimezone, alignmentForUtc, expandTimeWindowToInterval, } from "ee-utils";
import { DateTime } from "luxon";
export var TimeHorizon;
(function (TimeHorizon) {
    TimeHorizon["DAY"] = "DAY";
    TimeHorizon["WEEK"] = "WEEK";
    TimeHorizon["MONTH"] = "MONTH";
    TimeHorizon["YTD"] = "YTD";
    TimeHorizon["CUSTOM"] = "CUSTOM";
    TimeHorizon["ALL"] = "ALL";
})(TimeHorizon || (TimeHorizon = {}));
var DropDownBasedTimeHorizons = [
    TimeHorizon.DAY,
    TimeHorizon.MONTH,
    TimeHorizon.YTD,
];
export function getDateRange(horizon, startDate, inclusiveRange) {
    var start = new Date(startDate);
    var end = new Date(startDate);
    var window = { start: start, end: end };
    switch (horizon) {
        case TimeHorizon.DAY:
            if (inclusiveRange) {
                start.setUTCHours(0, 0, 0, 0);
                end.setUTCHours(23, 59, 59, 999);
            }
            else {
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
            }
            break;
        case TimeHorizon.WEEK:
            if (inclusiveRange) {
                var weekRange = expandTimeWindowToInterval(window, AggregationInterval.Week);
                start = weekRange.start;
                end = weekRange.end;
            }
            else {
                // Monday-based week, matching Luxon's ISO default used by the inclusiveRange path above.
                var isoWeekday = start.getDay() === 0 ? 7 : start.getDay();
                start = new Date(start.getFullYear(), start.getMonth(), start.getDate() - (isoWeekday - 1));
                end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
            }
            break;
        case TimeHorizon.MONTH:
            if (inclusiveRange) {
                var monthRange = expandTimeWindowToInterval(window, AggregationInterval.Month);
                start = monthRange.start;
                end = monthRange.end;
            }
            else {
                start = new Date(start.getFullYear(), start.getMonth(), 1);
                end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
            }
            break;
        case TimeHorizon.YTD:
            if (inclusiveRange) {
                var yearRange = expandTimeWindowToInterval(window, AggregationInterval.Year);
                start = yearRange.start;
                end = yearRange.end;
            }
            else {
                start = new Date(start.getFullYear(), 0, 1);
                end = new Date(start.getFullYear() + 1, 0, 1);
            }
            break;
        case TimeHorizon.ALL:
        default:
            end = new Date(start.getFullYear() + 1, 0, 1);
            start = new Date(0);
            break;
    }
    return [start, end];
}
export function GetHistoricalDateRanges(horizon, historicalPoints, startDate, inclusiveRange) {
    if (horizon == TimeHorizon.ALL) {
        // Max granularity is a year.
        horizon = TimeHorizon.YTD;
    }
    var ranges = [getDateRange(horizon, startDate, inclusiveRange)];
    for (var i = 0; i < historicalPoints - 1; ++i) {
        var start = new Date(ranges[i][0]);
        var end = new Date(ranges[i][1]);
        switch (horizon) {
            case TimeHorizon.DAY:
                start.setDate(start.getDate() - 1);
                end.setDate(end.getDate() - 1);
                break;
            case TimeHorizon.WEEK:
                start.setDate(start.getDate() - 7);
                end.setDate(end.getDate() - 7);
                break;
            case TimeHorizon.MONTH:
                start.setMonth(start.getMonth() - 1);
                end.setMonth(end.getMonth() - 1);
                break;
            default:
                start.setFullYear(start.getFullYear() - 1);
                end.setFullYear(end.getFullYear() - 1);
                break;
        }
        ranges.push([start, end]);
    }
    return ranges.reverse();
}
function horizonToInterval(horizon) {
    switch (horizon) {
        case TimeHorizon.DAY:
            return AggregationInterval.Day;
        case TimeHorizon.WEEK:
            return AggregationInterval.Week;
        case TimeHorizon.MONTH:
            return AggregationInterval.Month;
        default:
            return AggregationInterval.Year;
    }
}
function alignmentForDateRange(inclusiveRange) {
    return inclusiveRange
        ? alignmentForUtc()
        : alignmentForTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
}
export function getDateRangesBetweenDates(horizon, earliestDate, latestDate, inclusiveRange) {
    var _a;
    if (horizon == TimeHorizon.ALL) {
        // Max granularity is a year.
        horizon = TimeHorizon.YTD;
    }
    var interval = horizonToInterval(horizon);
    var alignment = alignmentForDateRange(inclusiveRange);
    var currentDate = getDateRange(horizon, earliestDate, inclusiveRange);
    var lastDate = getDateRange(horizon, latestDate, inclusiveRange);
    var ranges = [];
    while (currentDate[0].getTime() <= lastDate[0].getTime()) {
        ranges.push([new Date(currentDate[0]), new Date(currentDate[1])]);
        var nextDate = DateTime.fromJSDate(currentDate[0], {
            zone: alignment.timezoneName,
        })
            .plus((_a = {}, _a[interval] = 1, _a))
            .toJSDate();
        currentDate = getDateRange(horizon, nextDate, inclusiveRange);
    }
    return ranges.reverse();
}
export function getSubDateRanges(horizon, startDate) {
    var dateRange = getDateRange(horizon, startDate);
    switch (horizon) {
        default:
        case TimeHorizon.DAY:
            return Array(24)
                .fill(dateRange[0])
                .map(function (d, i) {
                var start = new Date(d);
                var end = new Date(d);
                start.setHours(i, 0, 0, 0);
                end.setHours(i + 1, 0, 0, 0);
                var display = "".concat(start.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                }), " - ").concat(end.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                }));
                return [start, end, display];
            });
        case TimeHorizon.WEEK:
            return Array(7)
                .fill(dateRange[0])
                .map(function (d, i) {
                var start = new Date(d);
                var end = new Date(d);
                start.setDate(start.getDate() + i);
                end.setDate(end.getDate() + i + 1);
                var display = start.toLocaleDateString(undefined, {
                    weekday: "short",
                });
                return [start, end, display];
            });
        case TimeHorizon.MONTH:
            return Array(new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0).getDate())
                .fill(dateRange[0])
                .map(function (d, i) {
                var start = new Date(d);
                var end = new Date(d);
                start.setDate(i + 1);
                end.setDate(i + 2);
                var display = "".concat(start.toLocaleDateString());
                return [start, end, display];
            });
        case TimeHorizon.YTD:
            return Array(12)
                .fill(dateRange[0])
                .map(function (d, i) {
                var start = new Date(d);
                var end = new Date(d);
                start.setMonth(i);
                end.setMonth(i + 1);
                var display = "".concat(start.toLocaleString("default", {
                    month: "long",
                }));
                return [start, end, display];
            });
    }
}
export var DateRange = function (_a) {
    var value = _a.value, setValue = _a.setValue, hideAll = _a.hideAll, inclusiveRange = _a.inclusiveRange;
    var range = getDateRange(value, new Date(), inclusiveRange);
    var formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: "UTC",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    var dateRangeText = value == TimeHorizon.DAY
        ? "".concat(formatter.format(range[0]))
        : "".concat(formatter.format(range[0]), " - ").concat(formatter.format(range[1]));
    var items = [
        { name: TimeHorizon.DAY, value: TimeHorizon.DAY },
        { name: TimeHorizon.WEEK, value: TimeHorizon.WEEK },
        { name: TimeHorizon.MONTH, value: TimeHorizon.MONTH },
        { name: TimeHorizon.YTD, value: TimeHorizon.YTD },
    ];
    if (!hideAll) {
        items.push({ name: TimeHorizon.ALL, value: TimeHorizon.ALL });
    }
    return (_jsxs("div", __assign({ className: "flex flex-row gap-x-[32px] items-center" }, { children: [_jsx(Radio, { items: items, value: value, setValue: setValue }), value != TimeHorizon.ALL && (_jsx("label", __assign({ className: "font-N8 text-black-400" }, { children: dateRangeText })))] })));
};
export var DateRangeSelector = function (_a) {
    var _b, _c;
    var dateRange = _a.dateRange, setDateRange = _a.setDateRange, onHorizonChanged = _a.onHorizonChanged, firstDate = _a.firstDate, hideAll = _a.hideAll, hideCustom = _a.hideCustom, hideYtd = _a.hideYtd, inclusiveRange = _a.inclusiveRange;
    var _d = useState(TimeHorizon.DAY), timeHorizon = _d[0], setTimeHorizon = _d[1];
    var setTimeHorizonAndResetDate = useCallback(function (horizon) {
        setTimeHorizon(horizon);
        if (onHorizonChanged) {
            onHorizonChanged(horizon);
        }
        setDateRange(getDateRange(horizon, new Date(), inclusiveRange));
    }, [getDateRange, onHorizonChanged, setDateRange, setTimeHorizon]);
    useEffect(function () {
        // This useEffect is used as an onComponentDidMount with the default state of TimeHorizon.DAY.
        setDateRange(getDateRange(timeHorizon, new Date(), inclusiveRange));
        if (onHorizonChanged) {
            onHorizonChanged(timeHorizon);
        }
    }, [getDateRange, onHorizonChanged, setDateRange, inclusiveRange]);
    var onCustomDateChange = useCallback(function (startDateText, endDateText) {
        var start = startDateText ? new Date(startDateText) : dateRange[0];
        start.setUTCHours(0, 0, 0, 0);
        var end = endDateText ? new Date(endDateText) : dateRange[1];
        end.setUTCHours(23, 59, 59, 999);
        setDateRange([start, end]);
    }, [dateRange, setDateRange]);
    var formatter = useMemo(function () {
        return new Intl.DateTimeFormat("en-US", {
            timeZone: "UTC",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        });
    }, []);
    var dateRangeToString = useCallback(function (horizon, range) {
        return horizon == TimeHorizon.DAY
            ? "".concat(formatter.format(range[0]))
            : "".concat(formatter.format(range[0]), " - ").concat(formatter.format(range[1]));
    }, [formatter]);
    var items = [
        { name: TimeHorizon.DAY, value: TimeHorizon.DAY },
        { name: TimeHorizon.MONTH, value: TimeHorizon.MONTH },
    ];
    if (!hideYtd) {
        items.push({ name: TimeHorizon.YTD, value: TimeHorizon.YTD });
    }
    if (!hideCustom) {
        items.push({ name: TimeHorizon.CUSTOM, value: TimeHorizon.CUSTOM });
    }
    if (!hideAll) {
        items.push({ name: TimeHorizon.ALL, value: TimeHorizon.ALL });
    }
    var historicalDropdownItems = useMemo(function () {
        if (!DropDownBasedTimeHorizons.includes(timeHorizon)) {
            return undefined;
        }
        var historicalRanges = getDateRangesBetweenDates(timeHorizon, firstDate, new Date(), inclusiveRange);
        var historicalDropdownItems = historicalRanges.map(function (r) {
            var stringName = dateRangeToString(timeHorizon, r);
            return {
                name: stringName,
                value: r,
                selectedLabel: stringName,
            };
        });
        return historicalDropdownItems;
    }, [
        dateRangeToString,
        getDateRangesBetweenDates,
        timeHorizon,
        firstDate,
        inclusiveRange,
    ]);
    return (_jsxs("div", __assign({ className: "flex flex-row gap-x-[32px] items-center" }, { children: [_jsx(Radio, { items: items, value: timeHorizon, setValue: setTimeHorizonAndResetDate }), DropDownBasedTimeHorizons.includes(timeHorizon) && (_jsx(Dropdown, { items: historicalDropdownItems, currentSelection: dateRange, onClick: setDateRange, comparator: function (d1, d2) {
                    return d1.every(function (val, i) { return val.getTime() == d2[i].getTime(); });
                } })), timeHorizon === TimeHorizon.CUSTOM && (_jsxs(_Fragment, { children: [_jsx(TextInput, { type: "date", onChange: function (e) {
                            return onCustomDateChange(e.target.value, undefined);
                        }, defaultValue: ((_b = dateRange[0]) === null || _b === void 0 ? void 0 : _b.toLocaleDateString()) ||
                            (firstDate === null || firstDate === void 0 ? void 0 : firstDate.toLocaleDateString()) ||
                            "" }), _jsx(TextInput, { type: "date", onChange: function (e) {
                            return onCustomDateChange(undefined, e.target.value);
                        }, defaultValue: ((_c = dateRange[1]) === null || _c === void 0 ? void 0 : _c.toLocaleDateString()) || "" })] }))] })));
};
